/**
 * alertEngine.js — Multi-tier, multi-symbol SPY-style signal engine
 *
 * One AlertEngine instance per symbol. Tier settings are shared (symbol-agnostic).
 * Each instance has independent indicator state (bars, persist counters, cooldowns).
 *
 * onAlert events:
 *   { type:'state',  symbol, state }
 *   { type:'signal', symbol, tierId, key, label, severity, detail, ... }
 */

import { BASE, getToken } from './api.js';
import { TIER_IDS, TIER_DEFAULTS } from './alertTiers.js';
import { detectCoil, analyzePreMarket } from './sessionRegime.js';

const RSI_PERIOD = 14;
const BB_PERIOD  = 20;
const POLL_MS    = 60_000;

// ── Math ──────────────────────────────────────────────────────────────────────

function calcRSI(closes) {
  if (closes.length < RSI_PERIOD + 1) return null;
  const s = closes.slice(-(RSI_PERIOD + 1));
  let g=0,l=0;
  for (let i=1;i<s.length;i++){const d=s[i]-s[i-1];if(d>0)g+=d;else l-=d;}
  const ag=g/RSI_PERIOD,al=l/RSI_PERIOD;
  if(al===0)return 100;
  return 100-100/(1+ag/al);
}

function calcBollinger(closes,sigma=1){
  if(closes.length<BB_PERIOD)return null;
  const s=closes.slice(-BB_PERIOD);
  const mean=s.reduce((a,b)=>a+b,0)/BB_PERIOD;
  const std=Math.sqrt(s.reduce((a,b)=>a+(b-mean)**2,0)/BB_PERIOD);
  return{upper:mean+sigma*std,lower:mean-sigma*std,mid:mean,std};
}

function calcVWAP(bars){
  let pv=0,v=0;
  for(const b of bars){const tp=(b.high+b.low+b.close)/3;pv+=tp*b.volume;v+=b.volume;}
  return v>0?pv/v:null;
}

function vwapDevPct(price,vwap){
  if(price==null||vwap==null)return null;
  return((price-vwap)/vwap)*100;
}

function calcConfluence({rsi,price,bb,vwap,vixPct}){
  let score=0,factors=[];
  if(rsi!=null){
    score+=-(rsi-50)/50*40;
    if(rsi<30)factors.push({label:'RSI oversold',bull:true,weight:3});
    if(rsi>70)factors.push({label:'RSI overbought',bull:false,weight:3});
  }
  if(price!=null&&vwap!=null){
    const dev=vwapDevPct(price,vwap);
    score+=-Math.max(-1,Math.min(1,dev/0.5))*30;
    if(dev<-0.3)factors.push({label:'Below VWAP',bull:true,weight:2});
    if(dev>0.3)factors.push({label:'Above VWAP',bull:false,weight:2});
  }
  if(bb!=null&&price!=null){
    const range=bb.upper-bb.lower;
    if(range>0)score+=Math.max(-20,Math.min(20,((price-bb.lower)/range-0.5)*-40));
    if(price<bb.lower)factors.push({label:'Below BB lower',bull:true,weight:2});
    if(price>bb.upper)factors.push({label:'Above BB upper',bull:false,weight:2});
  }
  if(vixPct!=null&&Math.abs(vixPct)>3){
    const w=vixPct>0?'VIX spiking':'VIX collapsing';
    factors.push({label:w,bull:vixPct<0,weight:1,warn:true});
  }
  score=Math.max(-100,Math.min(100,score));
  return{score,direction:score>15?'bullish':score<-15?'bearish':'neutral',factors};
}

// ── ATR ───────────────────────────────────────────────────────────────────────
const ATR_PERIOD = 14;
function calcATR(bars) {
  if (bars.length < ATR_PERIOD + 1) return null;
  const slice = bars.slice(-(ATR_PERIOD + 1));
  let sum = 0;
  for (let i = 1; i < slice.length; i++) {
    const h = slice[i].high   ?? slice[i].close;
    const l = slice[i].low    ?? slice[i].close;
    const p = slice[i-1].close;
    sum += Math.max(h - l, Math.abs(h - p), Math.abs(l - p));
  }
  return sum / ATR_PERIOD;
}

// ── ADX (14) ──────────────────────────────────────────────────────────────────
// ADX < 20 → ranging (good for scalp fade)
// ADX > 30 → trending (dangerous to fade)
const ADX_PERIOD = 14;
function calcADX(bars) {
  if (bars.length < ADX_PERIOD * 2) return null;
  const slice = bars.slice(-(ADX_PERIOD * 2));
  const plusDMs = [], minusDMs = [], trs = [];
  for (let i = 1; i < slice.length; i++) {
    const h  = slice[i].high    ?? slice[i].close;
    const l  = slice[i].low     ?? slice[i].close;
    const ph = slice[i-1].high  ?? slice[i-1].close;
    const pl = slice[i-1].low   ?? slice[i-1].close;
    const pc = slice[i-1].close;
    const up = h - ph, dn = pl - l;
    plusDMs.push(up > dn && up > 0 ? up : 0);
    minusDMs.push(dn > up && dn > 0 ? dn : 0);
    trs.push(Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc)));
  }
  const wilder = (arr) => {
    let s = arr.slice(0, ADX_PERIOD).reduce((a,b)=>a+b, 0);
    const r = [s];
    for (let i = ADX_PERIOD; i < arr.length; i++) { s = s - s/ADX_PERIOD + arr[i]; r.push(s); }
    return r;
  };
  const atr14 = wilder(trs), pDM14 = wilder(plusDMs), mDM14 = wilder(minusDMs);
  const dxs = atr14.map((a, i) => {
    if (!a) return 0;
    const pDI = pDM14[i]/a*100, mDI = mDM14[i]/a*100;
    const s = pDI+mDI; return s ? Math.abs(pDI-mDI)/s*100 : 0;
  });
  const adx  = dxs.slice(-ADX_PERIOD).reduce((a,b)=>a+b,0) / ADX_PERIOD;
  const last  = atr14.length-1;
  const pDI   = atr14[last] ? pDM14[last]/atr14[last]*100 : 0;
  const mDI   = atr14[last] ? mDM14[last]/atr14[last]*100 : 0;
  return { adx, pDI, mDI };
}

// ── BB Width % ────────────────────────────────────────────────────────────────
function calcBBWidth(bb) {
  if (!bb || !bb.mid) return null;
  return (bb.upper - bb.lower) / bb.mid * 100;
}

// ── RSI Slope (drift over last 5 bars) ───────────────────────────────────────
function calcRSISlope(closes) {
  const W = 5;
  if (closes.length < RSI_PERIOD + 1 + W) return null;
  const now  = calcRSI(closes);
  const prev = calcRSI(closes.slice(0, -W));
  return (now != null && prev != null) ? now - prev : null;
}

// ── Regime label for scalping ─────────────────────────────────────────────────
function getRegime(adxVal) {
  if (adxVal == null) return { label:'Unknown', color:'var(--faint)', scalp:'' };
  if (adxVal < 15)   return { label:'Strong Range', color:'var(--up)',   scalp:'Ideal — fade extremes confidently' };
  if (adxVal < 20)   return { label:'Ranging',      color:'var(--up)',   scalp:'Good — mean-reversion favoured' };
  if (adxVal < 25)   return { label:'Weak Trend',   color:'var(--warn)', scalp:'Caution — trend developing, tighten stops' };
  if (adxVal < 35)   return { label:'Trending',     color:'var(--down)', scalp:'Risky — avoid fading, cut losers quickly' };
  return               { label:'Strong Trend', color:'var(--down)', scalp:'Avoid mean-reversion — trend too strong' };
}

// Minutes elapsed since 9:30 AM ET (0 before open, capped at 390 at close)
function minutesIntoSessionET() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date());
  const p = Object.fromEntries(parts.map(x => [x.type, x.value]));
  const mins = Number(p.hour === '24' ? '0' : p.hour) * 60 + Number(p.minute);
  return Math.max(0, Math.min(390, mins - 570)); // 570 = 9:30 AM
}

function vixLevelToIVRank(vix){
  if(vix<13)return{rank:10,label:'Very low',note:'Avoid buying premium'};
  if(vix<16)return{rank:25,label:'Low',note:'Caution buying premium'};
  if(vix<20)return{rank:45,label:'Moderate',note:'Neutral environment'};
  if(vix<25)return{rank:60,label:'Elevated',note:'Favour buying premium'};
  if(vix<30)return{rank:75,label:'High',note:'Good for buying premium'};
  return{rank:90,label:'Extreme',note:'Favour short premium / spreads'};
}

function mkTierState(){
  return{
    persist:{oversold:0,overbought:0,bbLower:0,bbUpper:0},
    lastAlertAt:{oversold:0,overbought:0,bbLower:0,bbUpper:0},
  };
}

// ── Single-symbol engine ──────────────────────────────────────────────────────

export class AlertEngine {
  constructor({symbol,onAlert}){
    this.symbol    = symbol;
    this.onAlert   = onAlert;
    this.tiers     = {};
    this.bars      = [];
    this._vixPrice = null;
    this._vixPct   = null;
    this._timer    = null;
    this._running  = false;
    this._dailyBars   = [];     // daily bars for coil detection
    this._coil        = null;   // last coil-detection result
    this._preMarket   = null;   // pre-market / prior-day analysis
    this._dailyFetchAt= 0;      // timestamp of last daily fetch
    for(const id of TIER_IDS)this.tiers[id]={settings:{...TIER_DEFAULTS[id]},...mkTierState()};
  }

  updateTierSettings(tierId,s){
    if(!this.tiers[tierId])return;
    this.tiers[tierId].settings={...TIER_DEFAULTS[tierId],...s};
  }

  resetTier(tierId){
    if(!this.tiers[tierId])return;
    this.tiers[tierId].persist={oversold:0,overbought:0,bbLower:0,bbUpper:0};
    this.tiers[tierId].lastAlertAt={oversold:0,overbought:0,bbLower:0,bbUpper:0};
  }

  injectMarketData({vixPrice,vixPrevClose}){
    this._vixPrice=vixPrice;
    this._vixPct=vixPrice!=null&&vixPrevClose!=null&&vixPrevClose>0
      ?((vixPrice-vixPrevClose)/vixPrevClose)*100:null;
  }

  start(){
    if(this._running)return;
    this._running=true;
    this._tick();
    this._timer=setInterval(()=>this._tick(),POLL_MS);
  }

  stop(){
    this._running=false;
    clearInterval(this._timer);
    this._timer=null;
    this.bars=[];
    for(const id of TIER_IDS)this.resetTier(id);
  }

  async _tick(){
    // Fetch regardless of market hours — Yahoo returns bars outside hours too.
    // Signals only fire when persist counters accumulate (needs consecutive bars moving).
    // The auto-enable + reset logic in useAlertEngine handles market-open transitions.
    try{
      await this._fetchBars();
      // Refresh daily bars + coil detection at most every 30 min (daily data is slow)
      if(Date.now() - this._dailyFetchAt > 30*60_000){
        this._fetchDailyBars().catch(()=>{}); // non-blocking
      }
      this._evaluate();
    }catch(e){
      console.warn(`[AlertEngine:${this.symbol}] tick error:`,e.message);
      // Emit null state so UI shows error rather than "Fetching first bar…" forever
      this.onAlert({type:'state',symbol:this.symbol,state:null,fetchError:e.message});
    }
  }

  async _fetchDailyBars(){
    this._dailyFetchAt = Date.now();
    const r=await fetch(
      `${BASE}/chart?symbol=${encodeURIComponent(this.symbol)}&interval=1d&range=2mo`,
      {headers:{'x-session-token':getToken()}}
    );
    if(!r.ok) return;
    const data=await r.json();
    let bars=[];
    if(data.bars&&Array.isArray(data.bars)){
      bars=data.bars.filter(b=>b.c!=null)
        .map(b=>({time:b.t/1000,open:b.o,high:b.h,low:b.l,close:b.c,volume:b.v??0}));
    } else {
      const result=data?.chart?.result?.[0];
      if(result){
        const{timestamp:ts,indicators:{quote:[q]}}=result;
        bars=ts.map((t,i)=>({time:t,open:q.open[i],high:q.high[i],low:q.low[i],close:q.close[i],volume:q.volume[i]??0}))
          .filter(b=>b.close!=null);
      }
    }
    this._dailyBars=bars;
    this._coil=detectCoil(bars);
    // Pre-market analysis — uses latest intraday price as proxy for current/pre-market price
    const curPrice = this.bars.length ? this.bars[this.bars.length-1].close : null;
    this._preMarket = analyzePreMarket(bars, curPrice);
  }

  async _fetchBars(){
    const r=await fetch(
      `${BASE}/chart?symbol=${encodeURIComponent(this.symbol)}&interval=1m&range=1d`,
      {headers:{'x-session-token':getToken()}}
    );
    if(!r.ok)throw new Error(`chart ${r.status}`);
    const data=await r.json();
    // Worker returns transformed shape: { bars:[{t,o,h,l,c,v}], symbol, fetchedAt }
    // t is already in ms; fall back to raw Yahoo shape for resilience
    if(data.bars&&Array.isArray(data.bars)){
      this.bars=data.bars
        .filter(b=>b.c!=null)
        .map(b=>({time:b.t/1000,open:b.o,high:b.h,low:b.l,close:b.c,volume:b.v??0}))
        .slice(-50);
    } else {
      // Raw Yahoo fallback (if worker ever bypasses transformation)
      const result=data?.chart?.result?.[0];
      if(!result)throw new Error('no chart result');
      const{timestamp:ts,indicators:{quote:[q]}}=result;
      this.bars=ts
        .map((t,i)=>({time:t,open:q.open[i],high:q.high[i],low:q.low[i],close:q.close[i],volume:q.volume[i]??0}))
        .filter(b=>b.close!=null)
        .slice(-50);
    }
  }

  _evaluate(){
    const closes=this.bars.map(b=>b.close);
    const now=Date.now();

    // Emit partial state when not enough bars yet, so UI shows progress not a spinner
    if(closes.length<BB_PERIOD){
      const rsi=closes.length>=RSI_PERIOD+1?calcRSI(closes):null;
      const vwap=this.bars.length>0?calcVWAP(this.bars):null;
      const price=closes.length>0?closes[closes.length-1]:null;
      this.onAlert({
        type:'state',symbol:this.symbol,
        state:{rsi,vwap,vwapDev:vwapDevPct(price,vwap),price,
               vixPrice:this._vixPrice,vixPct:this._vixPct,
               confluence:{score:0,direction:'neutral',factors:[]},
               supplemental:{vwapDev:null,vixPrice:this._vixPrice,vixPct:this._vixPct,
                             vixSpike:false,ivRankProxy:null,putCallRatio:null},
               tierPersist:{},timestamp:now,
               barsLoaded:closes.length,barsNeeded:BB_PERIOD,
               coil:this._coil,preMarket:this._preMarket},
      });
      return;
    }

    const rsi     =calcRSI(closes);
    const vwap    =calcVWAP(this.bars);
    const price   =closes[closes.length-1];
    const vixPrice=this._vixPrice;
    const vixPct  =this._vixPct;
    const vwapDev =vwapDevPct(price,vwap);

    // BB cache — compute different sigmas lazily
    const bbCache={};
    const getBB=(sigma)=>{const k=String(sigma);if(!bbCache[k])bbCache[k]=calcBollinger(closes,sigma);return bbCache[k];};

    // Volatility / regime indicators
    const bb1     =getBB(1);
    const atr     =calcATR(this.bars);
    const atrPct  =atr!=null&&price>0 ? (atr/price)*100 : null;
    const adxData =calcADX(this.bars);
    const bbWidth =calcBBWidth(bb1);
    const rsiSlope=calcRSISlope(closes);
    const regime  =getRegime(adxData?.adx);


    const confluence=calcConfluence({rsi,price,bb:getBB(1),vwap,vixPct});
    const supplemental={
      vwapDev,vixPrice,vixPct,
      vixSpike:vixPct!=null&&Math.abs(vixPct)>=3,
      ivRankProxy:vixPrice!=null?vixLevelToIVRank(vixPrice):null,
      putCallRatio:null,
    };

    const tierPersist={};
    for(const id of TIER_IDS)tierPersist[id]={...this.tiers[id].persist};

    this.onAlert({
      type:'state',symbol:this.symbol,
      state:{rsi,vwap,vwapDev,price,vixPrice,vixPct,confluence,supplemental,tierPersist,timestamp:now,
             // Volatility / regime
             atr,atrPct,adx:adxData?.adx,pDI:adxData?.pDI,mDI:adxData?.mDI,
             bbWidth,rsiSlope,regime,
             // Raw bars + session timing for session-regime classifier
             bars:this.bars.map(b=>({time:b.time,open:b.open,high:b.high,low:b.low,close:b.close})),
             minutesIntoSession:minutesIntoSessionET(),
             coil:this._coil,preMarket:this._preMarket},
    });

    for(const tierId of TIER_IDS){
      const tier=this.tiers[tierId];
      const s=tier.settings;
      if(!s.enabled)continue;

      const bb=getBB(s.bbSigma??1);

      const conds={
        oversold: rsi!=null&&rsi<(s.rsiOversold??30),
        overbought:rsi!=null&&rsi>(s.rsiOverbought??70),
        bbLower:  bb!=null&&price<bb.lower,
        bbUpper:  bb!=null&&price>bb.upper,
      };
      for(const key of Object.keys(conds)){
        tier.persist[key]=conds[key]?tier.persist[key]+1:0;
      }

      const threshold =(s.persistBars??3);
      const cooldownMs=(s.cooldownMins??10)*60_000;
      const enabled   =s.enabledSignals??['oversold','overbought','bbLower','bbUpper'];

      const activeSignals=[];
      if(conds.oversold||conds.overbought)activeSignals.push('rsi');
      if(conds.bbLower||conds.bbUpper)    activeSignals.push('bb');
      if(vwapDev!=null&&Math.abs(vwapDev)>=(s.vwapDeviationPct??0.2))activeSignals.push('vwap');

      const minConf=s.minConfluence??1;
      if(activeSignals.length<minConf)continue;
      if(s.requireBBbreach&&!conds.bbLower&&!conds.bbUpper)continue;
      if(s.requireVWAPdev&&(vwapDev==null||Math.abs(vwapDev)<(s.vwapDeviationPct??0.2)))continue;

      const sym=this.symbol;
      const checks=[
        {key:'oversold',  label:'RSI Oversold',   severity:'bullish',
          detail:()=>`[${tierId}] ${sym} RSI ${rsi?.toFixed(1)}<${s.rsiOversold} · ${tier.persist.oversold}bars${vwapDev!=null&&vwapDev<-0.2?` · ${Math.abs(vwapDev).toFixed(2)}% below VWAP`:''}`},
        {key:'overbought',label:'RSI Overbought',  severity:'bearish',
          detail:()=>`[${tierId}] ${sym} RSI ${rsi?.toFixed(1)}>${s.rsiOverbought} · ${tier.persist.overbought}bars`},
        {key:'bbLower',   label:'BB Lower Breach', severity:'bullish',
          detail:()=>`[${tierId}] ${sym} $${price?.toFixed(2)} below BB lower $${bb?.lower?.toFixed(2)} · ${tier.persist.bbLower}bars`},
        {key:'bbUpper',   label:'BB Upper Breach', severity:'bearish',
          detail:()=>`[${tierId}] ${sym} $${price?.toFixed(2)} above BB upper $${bb?.upper?.toFixed(2)} · ${tier.persist.bbUpper}bars`},
      ];

      for(const c of checks){
        if(!enabled.includes(c.key))continue;
        if(tier.persist[c.key]<threshold)continue;
        if(tier.persist[c.key]%threshold!==0)continue;
        if(now-tier.lastAlertAt[c.key]<cooldownMs)continue;
        tier.lastAlertAt[c.key]=now;
        this.onAlert({
          type:'signal',symbol:sym,tierId,
          key:c.key,label:c.label,severity:c.severity,
          detail:c.detail(),rsi,price,bb,vwap,vwapDev,vixPrice,vixPct,
          confluence,confluenceCount:activeSignals.length,activeSignals,timestamp:now,
        });
      }
    }
  }
}
